import type { StateCreator } from 'zustand'
import { apiService } from '@/service/api'
import { readSSEMessages } from '@/utils/sse'
import { parseHelpAgentFrame } from '@/components/HelpAgent/protocol'
import { getTour } from '@/components/HelpAgent/TourOverlay/tourRegistry'

/**
 * State for the in-app help agent widget ("Akeera", components/HelpAgent).
 *
 * The SSE consumption loop lives HERE (not in the component) following the
 * `inAppMeetingSlice` precedent for slice-owned async lifecycle — the
 * widget mounts exactly once at the root layout, and slice-level streaming
 * is directly unit-testable with a mocked ReadableStream Response.
 */

export type HelpAgentStage = 'closed' | 'suggestions' | 'chatting' | 'rating' | 'thanks'

export interface HelpAgentMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  status: 'streaming' | 'complete' | 'error'
}

export interface HelpActiveTour {
  tourId: string
  stepIndex: number
}

export interface HelpPanelSize {
  width: number
  height: number
}

export const HELP_PANEL_MIN_WIDTH = 300
export const HELP_PANEL_MIN_HEIGHT = 420
export const HELP_PANEL_DEFAULT_SIZE: HelpPanelSize = { width: 320, height: 480 }

/**
 * Clamp a requested panel size to sane bounds: never below the Figma
 * baseline, never wider/taller than the viewport allows (margins keep the
 * header and launcher reachable). Pure + exported for tests.
 */
export function clampHelpPanelSize(
  size: HelpPanelSize,
  viewport: { width: number; height: number } = typeof window !== 'undefined'
    ? { width: window.innerWidth, height: window.innerHeight }
    : { width: 1440, height: 900 }
): HelpPanelSize {
  const maxWidth = Math.max(HELP_PANEL_MIN_WIDTH, Math.min(720, viewport.width - 48))
  const maxHeight = Math.max(HELP_PANEL_MIN_HEIGHT, viewport.height - 96)
  return {
    width: Math.round(Math.min(Math.max(size.width, HELP_PANEL_MIN_WIDTH), maxWidth)),
    height: Math.round(Math.min(Math.max(size.height, HELP_PANEL_MIN_HEIGHT), maxHeight)),
  }
}

export interface HelpAgentState {
  helpStage: HelpAgentStage
  helpMessages: HelpAgentMessage[]
  helpConversationId: string | null
  helpIsStreaming: boolean
  /** Tool currently running server-side (renders the "searching…" hint). */
  helpToolActivity: string | null
  helpInputValue: string
  /** Show the "Did this answer your question?" bubble after the answer. */
  helpFeedbackOffered: boolean
  helpRatings: Record<string, number>
  /** Set by a `navigate` directive; HelpAgentRoot performs + consumes it. */
  helpPendingNavigation: string | null
  /** Orthogonal to the panel stage — a tour can run with the panel closed. */
  helpActiveTour: HelpActiveTour | null
  /** User-resizable panel dimensions (drag handle on the panel corner). */
  helpPanelSize: HelpPanelSize

  openHelpAgent: () => void
  setHelpPanelSize: (size: HelpPanelSize) => void
  closeHelpAgent: () => void
  setHelpInputValue: (value: string) => void
  sendHelpMessage: (text: string, route: string) => Promise<void>
  /** `label`: the clicked chip's localized text — recorded in the thread
   *  as a user message so the conversation reflects the choice AFTER (and
   *  only after) the user actually makes it. */
  acceptHelpFeedbackPrompt: (label?: string) => void
  setHelpRating: (categoryId: string, value: number) => void
  submitHelpFeedback: (route?: string) => Promise<boolean>
  finishHelpFeedback: () => void
  startHelpTour: (tourId: string) => void
  advanceHelpTour: () => void
  endHelpTour: () => void
  consumeHelpNavigation: () => void
}

const STREAM_INACTIVITY_TIMEOUT_MS = 60_000

// Module scope (not store state) so devtools serialization stays clean.
let helpAbortController: AbortController | null = null

const makeId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `help-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

export const initialHelpAgentState: Pick<
  HelpAgentState,
  | 'helpStage'
  | 'helpMessages'
  | 'helpConversationId'
  | 'helpIsStreaming'
  | 'helpToolActivity'
  | 'helpInputValue'
  | 'helpFeedbackOffered'
  | 'helpRatings'
  | 'helpPendingNavigation'
  | 'helpActiveTour'
  | 'helpPanelSize'
> = {
  helpStage: 'closed',
  helpMessages: [],
  helpConversationId: null,
  helpIsStreaming: false,
  helpToolActivity: null,
  helpInputValue: '',
  helpFeedbackOffered: false,
  helpRatings: {},
  helpPendingNavigation: null,
  helpActiveTour: null,
  helpPanelSize: HELP_PANEL_DEFAULT_SIZE,
}

export const helpAgentSlice: StateCreator<
  HelpAgentState,
  [['zustand/devtools', never]],
  [],
  HelpAgentState
> = (set, get) => {
  const patchAssistantMessage = (
    messageId: string,
    patch: Partial<Pick<HelpAgentMessage, 'content' | 'status'>>
  ): void => {
    set({
      helpMessages: get().helpMessages.map((message) =>
        message.id === messageId ? { ...message, ...patch } : message
      ),
    })
  }

  return {
    ...initialHelpAgentState,

    openHelpAgent: () => {
      set({ helpStage: get().helpMessages.length > 0 ? 'chatting' : 'suggestions' })
    },

    closeHelpAgent: () => {
      // Keep messages/conversation so reopening resumes the thread; an
      // in-flight stream is aborted (the user navigated away from it).
      helpAbortController?.abort()
      set({ helpStage: 'closed' })
    },

    setHelpInputValue: (value) => set({ helpInputValue: value }),

    setHelpPanelSize: (size) => set({ helpPanelSize: clampHelpPanelSize(size) }),

    sendHelpMessage: async (text, route) => {
      const content = text.trim()
      if (content === '' || get().helpIsStreaming) return

      helpAbortController?.abort()
      const abortController = new AbortController()
      helpAbortController = abortController

      const userMessage: HelpAgentMessage = {
        id: makeId(),
        role: 'user',
        content,
        status: 'complete',
      }
      const assistantMessage: HelpAgentMessage = {
        id: makeId(),
        role: 'assistant',
        content: '',
        status: 'streaming',
      }
      set({
        helpStage: 'chatting',
        helpIsStreaming: true,
        helpToolActivity: null,
        helpFeedbackOffered: false,
        helpInputValue: '',
        helpMessages: [...get().helpMessages, userMessage, assistantMessage],
      })

      // Accumulate text deltas locally and flush at most once per animation
      // frame — markdown re-parsing per token locks up the main thread
      // (same mitigation ChatInterface applies to the main chat stream).
      let accumulated = ''
      let flushScheduled = false
      let pendingFrame: number | null = null
      const flush = (): void => {
        flushScheduled = false
        pendingFrame = null
        patchAssistantMessage(assistantMessage.id, { content: accumulated })
      }
      const scheduleFlush = (): void => {
        if (flushScheduled) return
        flushScheduled = true
        if (typeof requestAnimationFrame === 'function') {
          pendingFrame = requestAnimationFrame(flush)
        } else {
          flush()
        }
      }

      let sawError = false
      try {
        let conversationId = get().helpConversationId
        if (!conversationId) {
          const created = await apiService.createHelpConversation()
          conversationId = created.conversation_id
          set({ helpConversationId: conversationId })
        }

        // Lazy import avoids a config.ts → store import cycle at module load.
        const { getCurrentLanguage } = await import('@/i18n/config')
        const response = await apiService.sendHelpAgentMessage(
          conversationId,
          { content, route, language: getCurrentLanguage() },
          abortController.signal
        )

        for await (const event of readSSEMessages(response, {
          inactivityTimeoutMs: STREAM_INACTIVITY_TIMEOUT_MS,
        })) {
          if (event.event === 'done' || event.data === '[DONE]') break
          const frame = parseHelpAgentFrame(event.data)
          if (!frame) continue
          switch (frame.type) {
            case 'text':
              accumulated += frame.delta
              set({ helpToolActivity: null })
              scheduleFlush()
              break
            case 'tool_activity':
              set({ helpToolActivity: frame.toolName })
              break
            case 'help_action':
              if (frame.action.type === 'navigate') {
                set({ helpPendingNavigation: frame.action.route })
              } else {
                get().startHelpTour(frame.action.tourId)
              }
              break
            case 'error':
              sawError = true
              break
          }
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('[HelpAgent] stream failed:', error)
          sawError = true
        }
      } finally {
        // Cancel any rAF still queued so it can't re-run flush() after the
        // terminal status is set (greptile #18059).
        if (pendingFrame !== null && typeof cancelAnimationFrame === 'function') {
          cancelAnimationFrame(pendingFrame)
        }
        flush()
        const finalContent = accumulated
        const aborted = abortController.signal.aborted
        if (aborted) {
          // User closed/navigated mid-stream: drop an empty placeholder
          // bubble; keep any partial text but never mark it a clean answer.
          if (finalContent === '') {
            set({
              helpMessages: get().helpMessages.filter((m) => m.id !== assistantMessage.id),
            })
          } else {
            patchAssistantMessage(assistantMessage.id, { status: 'complete' })
          }
        } else if (sawError) {
          // Any error frame (even after partial text) marks the bubble as
          // errored — a truncated answer must not look complete.
          patchAssistantMessage(assistantMessage.id, { status: 'error' })
        } else {
          patchAssistantMessage(assistantMessage.id, { status: 'complete' })
        }
        if (helpAbortController === abortController) {
          helpAbortController = null
        }
        set({
          helpIsStreaming: false,
          helpToolActivity: null,
          // Offer "Did this answer your question?" only after a clean,
          // non-aborted, non-errored answer with content.
          helpFeedbackOffered: !aborted && finalContent !== '' && !sawError,
        })
      }
    },

    acceptHelpFeedbackPrompt: (label) => {
      set({
        helpStage: 'rating',
        helpFeedbackOffered: false,
        helpRatings: {},
        // Record the click as a real user message so the thread shows
        // "Yes, I'm done!" only after the user actually chose it.
        ...(label
          ? {
              helpMessages: [
                ...get().helpMessages,
                { id: makeId(), role: 'user' as const, content: label, status: 'complete' as const },
              ],
            }
          : {}),
      })
    },

    setHelpRating: (categoryId, value) => {
      set({ helpRatings: { ...get().helpRatings, [categoryId]: value } })
    },

    submitHelpFeedback: async (route) => {
      const { helpConversationId, helpRatings } = get()
      if (!helpConversationId || Object.keys(helpRatings).length === 0) return false
      try {
        await apiService.postHelpAgentFeedback(helpConversationId, {
          ratings: helpRatings,
          ...(route ? { route } : {}),
        })
        set({ helpStage: 'thanks' })
        return true
      } catch (error) {
        console.error('[HelpAgent] feedback submit failed:', error)
        return false
      }
    },

    finishHelpFeedback: () => {
      // "Done" closes out the guide: fresh thread on next question.
      set({
        helpStage: 'suggestions',
        helpRatings: {},
        helpMessages: [],
        helpConversationId: null,
        helpFeedbackOffered: false,
      })
    },

    startHelpTour: (tourId) => {
      if (!getTour(tourId)) {
        console.warn(`[HelpAgent] ignoring unknown tour id "${tourId}"`)
        return
      }
      // Close the panel so the spotlight isn't covered by the chat card.
      set({ helpActiveTour: { tourId, stepIndex: 0 }, helpStage: 'closed' })
    },

    advanceHelpTour: () => {
      const active = get().helpActiveTour
      if (!active) return
      const tour = getTour(active.tourId)
      if (!tour || active.stepIndex + 1 >= tour.steps.length) {
        set({ helpActiveTour: null })
        return
      }
      set({ helpActiveTour: { ...active, stepIndex: active.stepIndex + 1 } })
    },

    endHelpTour: () => set({ helpActiveTour: null }),

    consumeHelpNavigation: () => set({ helpPendingNavigation: null }),
  }
}
